import React from 'react';

import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { PromoProductTypes, getMinSpendProductTypeOptions, getProductTypeOptions } from '~/services/PromoService';

// Standalone MultiProductOptionSelect for modal use
interface StandaloneMultiProductOptionSelectProps {
  id: string;
  value: Array<string>;
  defaultValue?: Array<string>;
  filterFields?: Array<string>;
  showUpToXProducts?: number;
  promoProductTypes: PromoProductTypes;
  multiSelectType: 'products' | 'min_spend_products';
  onChange: (value: Array<string>) => void;
}

function StandaloneMultiProductOptionSelect({
  id,
  value,
  defaultValue,
  filterFields,
  showUpToXProducts = 2,
  promoProductTypes,
  multiSelectType,
  onChange,
}: StandaloneMultiProductOptionSelectProps) {
  // Use defaultValue when value is empty or undefined, fallback to empty array
  const effectiveValue = value && value.length > 0 ? value : defaultValue || [];

  const productOptions =
    multiSelectType == 'products'
      ? getProductTypeOptions(promoProductTypes, filterFields)
      : getMinSpendProductTypeOptions(promoProductTypes, filterFields);

  const productTypeOptions = productOptions.map((opt) => ({
    isChecked: effectiveValue.includes(opt.value),
    label: opt.label,
    value: opt.value,
  }));

  const renderValue = (selected: Array<string>) => {
    const matches = productTypeOptions.filter((opt) => selected.includes(opt.value));
    if (matches.length == 1) {
      return matches[0].label;
    } else if (matches.length > showUpToXProducts) {
      return `${selected.length} Products Selected`;
    } else {
      return `${matches.map((m) => m.label).join(', ')}`; // Show placeholder when nothing is selected
    }
  };

  const handleChange = (event: SelectChangeEvent<Array<string>>) => {
    const newValue = event.target.value;
    onChange(newValue as Array<string>);
  };

  return (
    <FormControl fullWidth>
      <InputLabel id={`${multiSelectType}-label`}>Products</InputLabel>
      <Select
        labelId={`${multiSelectType}-label`}
        id={id}
        multiple
        value={effectiveValue}
        onChange={handleChange}
        renderValue={(value: unknown) => {
          if (Array.isArray(value)) {
            return renderValue(value);
          }
          return '';
        }}
        label={multiSelectType === 'products' ? 'Select Products' : 'Select Min Spend Products'}
      >
        {productTypeOptions.map((opt, i) => (
          <MenuItem key={`${i}_${opt.value}`} value={opt.value}>
            <Checkbox checked={opt.isChecked} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default StandaloneMultiProductOptionSelect;
